import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { inviteMember, TeamMember } from '@/services/teamService';
import { Role } from '@/types/team';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTeampageTranslations } from '@/hooks/useTeampageTranslations';
import { logError, logDebug } from '@/utils/errorHandler';

interface InviteUserModalProps {
  show: boolean;
  onHide: () => void;
  roles: Role[];
  onSuccess?: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ show, onHide, roles, onSuccess }) => {
  // Get translations
  const i18n = useTeampageTranslations();

  const [email, setEmail] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // Get organization ID from auth context
  const { user } = useAuth();
  const orgId = user?.organization_id;

  // Filter out OrgOwner role (cannot be assigned manually)
  const selectableRoles = roles.filter(role => role.name !== 'OrgOwner');

  // Form validation
  const isEmailValid = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canSubmit = selectedRoleId > 0 && isEmailValid(email);

  // Invite mutation
  const inviteMutation = useMutation<TeamMember, Error, { email: string; roleId: number }>({
    mutationFn: ({ email, roleId }) => {
      if (!orgId) {
        throw new Error(i18n.errors.organizationIdMissing);
      }
      return inviteMember(email, roleId, orgId);
    },
    onSuccess: (data: any) => {
      setEmail('');
      setSelectedRoleId(0);
      setError(null);

      // Check email status from the response
      const emailStatus = data?.email_status;

      if (emailStatus === 'sent') {
        toast.success(i18n.inviteModal.invitationSent(email));
      } else if (emailStatus === 'mocked') {
        toast.success(
          `${i18n.inviteModal.invitationSent(email)} (Development Mode - Email logged to console)`
        );
      } else if (emailStatus === 'failed') {
        toast.warning(
          `User invited successfully, but email delivery failed. Please contact the user directly or resend the invitation.`
        );
      } else {
        // Fallback for older API responses without email_status
        toast.success(i18n.inviteModal.invitationSent(email));
      }

      onHide();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (err: any) => {
      logError(err instanceof Error ? err : new Error(String(err)), {
        component: 'InviteUserModal',
        action: 'inviteMutation',
        metadata: {
          email,
          selectedRoleId,
          orgId,
          context: 'user_invitation_error',
        },
      });

      let errorMessage = i18n.inviteModal.failedToSend;

      // Handle different error formats
      if (err.response) {
        // Axios error with response
        const { data } = err.response;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && typeof data === 'object') {
          if (data.detail) {
            errorMessage = data.detail;
          } else if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          } else if (data.email) {
            errorMessage = `Email: ${data.email}`;
          }
        }
      } else if (err.message) {
        // Regular error object
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(i18n.inviteModal.emailRequired);
      return;
    }

    if (!selectedRoleId) {
      setError(i18n.inviteModal.selectRole);
      return;
    }

    logDebug('Sending single invite to API', {
      email: email.trim(),
      roleId: selectedRoleId,
      orgId,
    });

    inviteMutation.mutate({
      email: email.trim(),
      roleId: selectedRoleId,
    });
  };

  const handleClose = () => {
    inviteMutation.reset(); // Reset mutation state on close
    setEmail('');
    setSelectedRoleId(0);
    setError(null);
    onHide();
  };

  if (!show) return null;

  return (
    <Dialog open={show} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{i18n.inviteModal.title}</DialogTitle>
          <DialogDescription>{i18n.inviteModal.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 rounded-md bg-destructive/15 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{i18n.inviteModal.emailLabel}</Label>
              <Input
                id="email"
                type="email"
                placeholder={i18n.inviteModal.emailPlaceholder}
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={inviteMutation.isPending}
                required
              />
              <p className="text-sm text-muted-foreground">{i18n.inviteModal.emailHelp}</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="role">{i18n.inviteModal.roleLabel}</Label>
              <Select
                value={selectedRoleId.toString()}
                onValueChange={value => setSelectedRoleId(parseInt(value, 10))}
                disabled={inviteMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder={i18n.inviteModal.rolePlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {selectableRoles.map(role => (
                    <SelectItem key={role.id} value={role.id.toString()}>
                      {i18n.roles[role.name as keyof typeof i18n.roles]?.name || role.name}
                      {role.description &&
                        ` - ${i18n.roles[role.name as keyof typeof i18n.roles]?.description || role.description}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={inviteMutation.isPending}>
                {i18n.inviteModal.cancel}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={inviteMutation.isPending || !canSubmit}>
              {inviteMutation.isPending
                ? i18n.inviteModal.sending
                : i18n.inviteModal.sendInvitation}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserModal;
